/**
 * VoiceCallCard — a call handed over by the AI, shown inside its Inbox
 * conversation (module voice_desk).
 *
 * Ringing: who is calling, why the AI is handing over, its summary, Answer.
 * On call: timer, whether the caller's voice is coming through, mute, hang up.
 * Renders nothing for conversations without a live handoff.
 */
import React, { useEffect, useState } from 'react';
import { LuHeadset, LuMic, LuMicOff, LuPhoneIncoming, LuPhoneOff } from 'react-icons/lu';

import Button from '../../../components/ui/Button';
import useTranslation from '../../../hooks/useTranslation';
import {
  answer, dismiss, enableAudio, handoffForConversation, hangUp, toggleMute, useVoiceDesk,
} from '../../../services/voiceDesk';

export const elapsed = (iso, now) => {
  if (!iso) return '';
  const s = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const useNow = (active) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
};

const VoiceCallCard = ({ conversationId }) => {
  const { t } = useTranslation();
  const desk = useVoiceDesk();
  const handoff = handoffForConversation(desk, conversationId);
  const onCall = Boolean(desk.call && handoff && desk.call.handoff.handoff_id === handoff.handoff_id);
  const now = useNow(Boolean(handoff));

  if (!handoff) return null;
  const live = onCall && desk.call.state === 'live';
  const busyElsewhere = Boolean(desk.call) && !onCall;

  return (
    <section
      className={`mx-4 mt-3 rounded-[var(--cx-radius)] border ${onCall ? 'border-success bg-surface' : 'border-accent bg-accent-soft'} px-4 py-3 flex flex-col gap-2`}
      aria-label={t('voice.desk_phone')}
      role={onCall ? 'region' : 'alert'}
    >
      <div className="flex items-center gap-3">
        <span className={`relative flex items-center justify-center w-10 h-10 rounded-full shrink-0 text-lg ${onCall ? 'bg-canvas-3 text-success' : 'bg-surface text-accent-dark'}`} aria-hidden>
          {!onCall && <span className="motion-safe:animate-ping absolute inset-0 rounded-full bg-accent opacity-20" />}
          <LuHeadset className="relative" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-ink">
            {onCall ? (live ? t('voice.desk_on_call') : t('voice.desk_connecting')) : t('voice.desk_incoming')}
          </div>
          <div className="text-xs text-text-2 truncate">
            {handoff.call?.agent ? t('voice.desk_from_ai', { agent: handoff.call.agent }) : t('voice.desk_from_ai_generic')}
            {handoff.call?.from_number && <span dir="ltr" className="tabular-nums"> · {handoff.call.from_number}</span>}
          </div>
        </div>
        <span className="text-xs font-semibold text-text-2 tabular-nums" dir="ltr" title={onCall ? undefined : t('voice.desk_waiting_for')}>
          {elapsed(onCall ? handoff.accepted_at : handoff.requested_at, now)}
        </span>
      </div>

      {handoff.reason && (
        <div className="text-xs"><span className="rounded-full px-2 py-0.5 bg-canvas-3 text-ink font-semibold">{handoff.reason}</span></div>
      )}
      {handoff.summary && <p className="text-sm text-ink-2 leading-relaxed m-0">{handoff.summary}</p>}

      {live && (
        <div className="flex items-center gap-2 text-xs text-text-2" role="status" aria-live="polite">
          <span className={`inline-block w-2 h-2 rounded-full ${desk.callerSpeaking ? 'bg-success' : desk.callerAudio ? 'bg-line-3' : 'bg-warning'}`} aria-hidden />
          <span>{desk.callerSpeaking ? t('voice.desk_caller_speaking') : desk.callerAudio ? t('voice.desk_caller_connected') : t('voice.desk_no_caller_audio')}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {onCall ? (
          <>
            {desk.audioBlocked && <Button size="sm" variant="secondary" onClick={enableAudio}>{t('voice.desk_enable_audio')}</Button>}
            <Button variant="secondary" size="sm" icon={desk.muted ? <LuMicOff /> : <LuMic />} onClick={toggleMute} disabled={!live} aria-pressed={desk.muted}>
              {desk.muted ? t('voice.desk_unmute') : t('voice.desk_mute')}
            </Button>
            <Button variant="danger" size="sm" className="ms-auto" icon={<LuPhoneOff />} onClick={() => hangUp()}>{t('voice.hang_up')}</Button>
          </>
        ) : (
          <>
            <Button size="sm" icon={<LuPhoneIncoming />} loading={desk.answering === handoff.handoff_id}
                    disabled={desk.answering !== null || busyElsewhere} onClick={() => answer(handoff)}>
              {t('voice.desk_answer')}
            </Button>
            {!desk.dismissed.includes(handoff.handoff_id) && (
              <Button variant="ghost" size="sm" onClick={() => dismiss(handoff.handoff_id)}>{t('voice.desk_not_now')}</Button>
            )}
            {busyElsewhere && <span className="text-xs text-text-2">{t('voice.desk_busy_on_call')}</span>}
          </>
        )}
      </div>
    </section>
  );
};

/**
 * VoiceCallStrip — top of the Inbox chat list: calls ringing for an agent, and
 * this agent's own call, whenever that conversation is not the one open.
 */
export const VoiceCallStrip = ({ activeConversationId, onOpen }) => {
  const { t } = useTranslation();
  const desk = useVoiceDesk();
  const now = useNow(Boolean(desk.call) || desk.waiting.length > 0);
  const rows = [];
  if (desk.call && desk.call.handoff.conversation_id !== activeConversationId) rows.push({ handoff: desk.call.handoff, mine: true });
  if (!desk.call) {
    desk.waiting.filter((h) => h.conversation_id !== activeConversationId).forEach((handoff) => rows.push({ handoff, mine: false }));
  }
  if (!rows.length) return null;
  return (
    <div className="flex flex-col gap-1 px-3 pt-3" role="list" aria-label={t('voice.desk_phone')}>
      {rows.map(({ handoff, mine }) => (
        <button key={handoff.handoff_id} type="button" role="listitem" onClick={() => onOpen(handoff.conversation_id)}
                className={`w-full flex items-center gap-2 text-start rounded-[var(--cx-radius)] border px-3 py-2 cursor-pointer ${mine ? 'border-success bg-surface' : 'border-accent bg-accent-soft'}`}>
          <LuHeadset className={mine ? 'text-success shrink-0' : 'text-accent-dark shrink-0'} aria-hidden />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-ink truncate">
              {handoff.call?.caller || handoff.call?.from_number || t('voice.desk_unknown_caller')}
            </span>
            <span className="block text-xs text-text-2 truncate">{mine ? t('voice.desk_on_call') : t('voice.desk_incoming')}</span>
          </span>
          <span className="text-xs font-semibold text-text-2 tabular-nums" dir="ltr">
            {elapsed(mine ? handoff.accepted_at : handoff.requested_at, now)}
          </span>
        </button>
      ))}
    </div>
  );
};

export default VoiceCallCard;
